import {Ref, ref} from 'vue'
import {post} from "@/api/phone";
import {FormInline} from "@/components/h_tb_header/tb_header_data";
import {ElLoading, ElMessage} from "element-plus";

interface Tb {
    "idCardNo": string,
    "phone": string,
    "price": string,
    "transAmt": string,
    "rePayTime": string | null,
    "payTime": string,
    "state": string,
    "type": string,
    "reqSeqId": string,
    "reqDate": string,
    "gong":string
}

interface PageResult {
    total: number;
    pages: number;
    pageSize: number;
    list: Tb[];
}

export function useCheckPage(
    tableData: Ref<Tb[]>,
    formInline: Ref<FormInline>,
    options?: {
        currentPage?: Ref<number>
        pageSize?: Ref<number>
        total?: Ref<number>
    },
    callback?: () => void
) {
    const currentPage = options?.currentPage ?? ref(1)
    const pageSize = options?.pageSize ?? ref(10)
    const total = options?.total ?? ref(0)

    const onSubmit = async () => {
        try {
            // console.log(formInline.value.date)
            if (formInline.value.date == null) {
                formInline.value.date = ["", ""]
            }
            const loadingInstance = ElLoading.service({fullscreen: true})
            const response = await post<PageResult | Tb[]>('/hbank/bankAI_oder', {
                ...formInline.value,
                pageNum: currentPage.value,
                pageSize: pageSize.value
            });
            console.log('提交成功:', response.data);

            if (Array.isArray(response.data)) {
                tableData.value = response.data;
            } else if (response.data && typeof response.data === 'object' && 'list' in response.data && Array.isArray(response.data.list)) {
                tableData.value = response.data.list;
                total.value = response.data.total || 0;
            } else {
                console.error('API 返回的数据格式异常:', response.data);
                ElMessage.error(response.msg)
                ElMessage.error(response.msg)
            }
            loadingInstance.close();
            callback?.();
        } catch (error) {
            console.error('提交失败:', error);
            // loadingInstance.close();
        }
    };

    const handlePageChange = (page: number, size: number) => {
        currentPage.value = page;
        pageSize.value = size;
        onSubmit();
    };

    const handleExport = async () => {
        if (total.value === 0) {
            ElMessage.warning('暂无数据可导出')
            return
        }

        const loadingInstance = ElLoading.service({fullscreen: true, text: '正在导出...'})

        try {
            // 获取所有页的数据
            const allData: Tb[] = []
            const currentSize = pageSize.value
            const totalPages = Math.ceil(total.value / currentSize)

            // 逐页请求数据
            for (let page = 1; page <= totalPages; page++) {
                const response = await post<PageResult>('/hbank/bank_oder', {
                    ...formInline.value,
                    pageNum: page,
                    pageSize: pageSize.value
                })

                if (response.data.list && Array.isArray(response.data.list)) {
                    allData.push(...response.data.list)
                }
            }
            // 定义导出列配置
            const columns = [
                {key: 'idCardNo', label: '身份证号'},
                {key: 'phone', label: '手机号'},
                {key: 'price', label: '金额'},
                {key: 'transAmt', label: '交易金额'},
                {key: 'rePayTime', label: '还款时间'},
                {key: 'payTime', label: '支付时间'},
                {key: 'state', label: '状态'},
                {key: 'type', label: '类型'},
                {key: 'reqSeqId', label: '请求序列号'},
                {key: 'reqDate', label: '请求日期'}
            ]

            // 状态映射
            const stateMap: Record<string, string> = {
                '0': '支付成功',
                '3': '退款中',
                '4': '已退款',
                '5': '退款失败',
                '6': '已退订'
            }

            // 类型映射
            const typeMap: Record<string, string> = {
                '1': '宝付',
                '0': '汇付'
            }

            // 时间格式化函数：转换为年月日时分秒格式
            const formatDateTime = (dateStr: string | null): string => {
                if (!dateStr) return ''

                // 格式1: 20260512135144 -> 2026-05-12 13:51:44
                if (/^\d{14}$/.test(dateStr)) {
                    const year = dateStr.slice(0, 4)
                    const month = dateStr.slice(4, 6)
                    const day = dateStr.slice(6, 8)
                    const hour = dateStr.slice(8, 10)
                    const minute = dateStr.slice(10, 12)
                    const second = dateStr.slice(12, 14)
                    return `${year}-${month}-${day} ${hour}:${minute}:${second}`
                }

                // 格式2: 20260512 -> 2026-05-12 00:00:00
                if (/^\d{8}$/.test(dateStr)) {
                    const year = dateStr.slice(0, 4)
                    const month = dateStr.slice(4, 6)
                    const day = dateStr.slice(6, 8)
                    return `${year}-${month}-${day} 00:00:00`
                }

                // 格式3: 2026-05-12 11:54:30.0 -> 2026-05-12 11:54:30
                if (dateStr.includes('.')) {
                    return dateStr.split('.')[0]
                }

                return dateStr
            }

            // 构建 CSV 内容
            const headers = columns.map(col => col.label).join(',')
            const rows = allData.map(row =>
                columns.map(col => {
                    let value = row[col.key as keyof Tb] ?? ''
                    // 时间字段格式化
                    if (col.key === 'rePayTime' || col.key === 'payTime' || col.key === 'reqDate') {
                        value = formatDateTime(value as string)
                    }
                    // 状态和类型字段转换为中文
                    if (col.key === 'state') {
                        value = stateMap[value as string] || value
                    }
                    if (col.key === 'type') {
                        value = typeMap[value as string] || value
                    }
                    // 处理包含逗号或换行的值
                    if (typeof value === 'string' && (value.includes(',') || value.includes('\n'))) {
                        value = `"${value.replace(/"/g, '""')}"`
                    }
                    return value
                }).join(',')
            )

            const csvContent = [headers, ...rows].join('\n')

            // 创建 Blob 并下载
            const blob = new Blob(['\ufeff' + csvContent], {type: 'text/csv;charset=utf-8;'})
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)

            link.setAttribute('href', url)
            link.setAttribute('download', `订单数据_${new Date().toISOString().slice(0, 10)}.csv`)
            link.style.visibility = 'hidden'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            ElMessage.success(`导出成功，共 ${allData.length} 条数据`)
        } catch (error) {
            console.error('导出失败:', error)
            ElMessage.error('导出失败')
        } finally {
            loadingInstance.close()
        }
    };

    return {
        currentPage,
        pageSize,
        total,
        onSubmit,
        handlePageChange,
        handleExport
    }
}
